import { motion, useInView } from 'framer-motion';
import { useRef, useState, useEffect } from 'react';
import axios from 'axios';
import { ArrowRight, Calendar, User, X, Loader2 } from 'lucide-react';
import { WordsPullUpMultiStyle } from '../components/AnimationComponents';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const resolveImageUrl = (url) => {
  if (!url) return url;
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) return url;
  return `${BACKEND_URL}${url}`;
};

export const BlogsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBlog, setSelectedBlog] = useState(null);

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const response = await axios.get(`${API_URL}/blogs`);
        setBlogs(response.data);
      } catch (error) {
        console.error('Error fetching blogs:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchBlogs();
  }, []);

  return (
    <section id="blogs" className="min-h-screen relative py-12 md:py-20 px-4 md:px-8" data-testid="blogs-section">
      <div className="relative z-10 max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12 md:mb-16">
          <WordsPullUpMultiStyle
            segments={[
              { text: "Daily Stories from", className: "text-xl sm:text-2xl md:text-3xl lg:text-4xl font-normal text-white drop-shadow-lg" },
              { text: "The Field", className: "text-xl sm:text-2xl md:text-3xl lg:text-4xl font-normal text-[#87CEEB] drop-shadow-lg" }
            ]}
            containerClassName="mb-4"
          />
          <p className="text-white/90 text-sm md:text-base max-w-2xl mx-auto drop-shadow">
            Follow our journey — the challenges, the process, and the achievements of our team at The Hygiene Company.
          </p>
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-white" />
          </div>
        )}

        {/* Blog Grid */}
        {!loading && blogs.length === 0 && (
          <div className="text-center py-12">
            <p className="text-white/80">No stories yet — check back soon!</p>
          </div>
        )}

        {!loading && blogs.length > 0 && (
          <div ref={ref} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-3 md:gap-2">
            {blogs.map((blog, i) => (
              <motion.div
                key={blog.id}
                initial={{ scale: 0.95, opacity: 0 }}
                animate={isInView ? { scale: 1, opacity: 1 } : {}}
                transition={{
                  duration: 0.6,
                  delay: i * 0.15,
                  ease: [0.22, 1, 0.36, 1]
                }}
                className="bg-black/40 backdrop-blur-md border border-white/20 rounded-2xl overflow-hidden lg:h-[480px] flex flex-col group cursor-pointer hover:bg-black/50 transition-colors"
                onClick={() => setSelectedBlog(blog)}
                data-testid={`blog-card-${i}`}
              >
                <div className="h-48 overflow-hidden">
                  <img
                    src={resolveImageUrl(blog.image)}
                    alt={blog.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>

                <div className="flex-1 p-6 flex flex-col">
                  <div className="flex items-center gap-3 text-gray-300 text-xs mb-3">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {new Date(blog.date).toLocaleDateString()}
                    </span>
                    <span className="flex items-center gap-1">
                      <User className="w-3 h-3" />
                      {blog.author}
                    </span>
                  </div>

                  <h3 className="text-white font-bold text-lg mb-2 line-clamp-2 drop-shadow">
                    {blog.title}
                  </h3>

                  <p className="text-gray-300 text-sm flex-1 line-clamp-3">
                    {blog.excerpt}
                  </p>

                  <div className="mt-4">
                    <span className="text-[#87CEEB] text-xs px-3 py-1 bg-[#87CEEB]/20 rounded-full">
                      {blog.category}
                    </span>
                  </div>

                  <button className="mt-4 text-white text-sm flex items-center gap-2 hover:gap-3 transition-all drop-shadow">
                    Read more
                    <ArrowRight className="w-4 h-4 transform -rotate-45" />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Blog Detail Modal */}
        {selectedBlog && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedBlog(null)}
            data-testid="blog-modal"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="bg-white rounded-3xl max-w-3xl w-full max-h-[90vh] overflow-y-auto p-8"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-start mb-6">
                <div className="flex-1 pr-4">
                  <span className="inline-block text-[#87CEEB] text-xs px-3 py-1 bg-[#87CEEB]/10 rounded-full mb-3">
                    {selectedBlog.category}
                  </span>
                  <h2 className="text-3xl font-bold text-gray-900 mb-2">{selectedBlog.title}</h2>
                  <div className="flex items-center gap-4 text-gray-600 text-sm">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {new Date(selectedBlog.date).toLocaleDateString()}
                    </span>
                    <span className="flex items-center gap-1">
                      <User className="w-4 h-4" />
                      {selectedBlog.author}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedBlog(null)}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0"
                  data-testid="close-blog-modal"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <img
                src={resolveImageUrl(selectedBlog.image)}
                alt={selectedBlog.title}
                className="w-full h-64 object-cover rounded-2xl mb-6"
              />

              <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                {selectedBlog.content}
              </p>
            </motion.div>
          </motion.div>
        )}
      </div>
    </section>
  );
};
